import { useState, useEffect, useMemo, useCallback } from 'react';
import { buildBackendUrl } from '../config/api-config';
const TOKEN_KEY = 'textile_erp_token';
const STORAGE_KEY = 'textile_erp_user_menus';
/**
 * Get cached menu data from localStorage
 */
function getMenuDataFromCache() {
    try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (!cached)
            return null;
        return JSON.parse(cached);
    }
    catch {
        return null;
    }
}
/**
 * Find menu by path in the menu tree (recursive search)
 * Returns the menu node with the actual database ID
 * Using path is more reliable than name since paths rarely change (tied to routes)
 */
function findMenuByPath(menus, menuPath) {
    for (const menu of menus) {
        if (menu.path === menuPath) {
            return menu;
        }
        if (menu.children && menu.children.length > 0) {
            const found = findMenuByPath(menu.children, menuPath);
            if (found)
                return found;
        }
    }
    return null;
}
/**
 * Fetch user's scopes for a specific menu by menuId from the API
 */
async function fetchMenuScopesById(menuId) {
    const token = localStorage.getItem(TOKEN_KEY);
    const response = await fetch(buildBackendUrl(`/api/identity/users/me/scopes-by-menu/${menuId}`), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch menu scopes: ${response.status}`);
    }
    return response.json();
}
/**
 * Hook to check user scopes/permissions for a specific menu
 *
 * @param menuPathOrId - The menu path (e.g., '/parking/vehicle-types') or menu ID to check scopes for
 * @param options - Optional configuration object
 * @param options.isMenuId - If true, menuPathOrId is treated as a menu ID (skips path lookup)
 * @returns Object with permission flags and loading state
 *
 * @example
 * // Using path (looks up menu ID from cached menu tree)
 * const { canViewAll, canAdd, canEdit, canDelete, isLoading } = useScopes('/parking/vehicle-types');
 *
 * // Using menu ID directly (for sub-menus loaded from API)
 * const { canViewAll, canAdd, canEdit, canDelete, isLoading } = useScopes(menuId, { isMenuId: true });
 *
 * // In JSX:
 * {canAdd && <Button onClick={handleAdd}>Add New</Button>}
 * {canEdit && <Button onClick={handleEdit}>Edit</Button>}
 */
export function useScopes(menuPathOrId, options) {
    const [menuScopes, setMenuScopes] = useState([]);
    const [menuId, setMenuId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [isAdminUser, setIsAdminUser] = useState(false);
    const isMenuId = options?.isMenuId ?? false;
    // Fetch user scopes for this specific menu
    useEffect(() => {
        let mounted = true;
        let retryTimeout = null;
        const loadScopes = async () => {
            try {
                setIsLoading(true);
                if (!menuPathOrId) {
                    throw new Error('Menu path or ID is required');
                }
                let foundMenuId = null;
                // Check if user is admin from cached menu data
                const cachedData = getMenuDataFromCache();
                if (cachedData?.isAdmin && mounted) {
                    setIsAdminUser(true);
                }
                if (isMenuId) {
                    // Menu ID passed directly - no lookup needed
                    foundMenuId = menuPathOrId;
                }
                else {
                    // Detect if it's a menu name (old format) or menu path (new format)
                    const isMenuName = !menuPathOrId.startsWith('/');
                    // Get menu data from cache
                    const menuData = cachedData;
                    // If no menu data in cache yet, retry after a short delay (max 5 retries)
                    if (!menuData && retryCount < 5) {
                        if (mounted) {
                            retryTimeout = setTimeout(() => {
                                if (mounted) {
                                    setRetryCount(prev => prev + 1);
                                }
                            }, 200); // Retry every 200ms
                        }
                        return;
                    }
                    if (isMenuName) {
                        // OLD FORMAT: Menu name like 'parking-vehicle-types'
                        // Need to find menu by name in the tree
                        if (menuData?.menus) {
                            const findMenuByName = (menus, targetName) => {
                                for (const menu of menus) {
                                    if (menu.name === targetName) {
                                        return menu;
                                    }
                                    if (menu.children && menu.children.length > 0) {
                                        const found = findMenuByName(menu.children, targetName);
                                        if (found)
                                            return found;
                                    }
                                }
                                return null;
                            };
                            const menu = findMenuByName(menuData.menus, menuPathOrId);
                            if (menu) {
                                foundMenuId = menu.id;
                            }
                        }
                    }
                    else {
                        // NEW FORMAT: Menu path like '/parking/vehicle-types'
                        if (menuData?.pathToMenuId) {
                            // Try direct path match first
                            foundMenuId = menuData.pathToMenuId[menuPathOrId] || null;
                        }
                        // Fallback: search in menu tree if pathToMenuId doesn't have it
                        if (!foundMenuId && menuData?.menus) {
                            const menu = findMenuByPath(menuData.menus, menuPathOrId);
                            if (menu) {
                                foundMenuId = menu.id;
                            }
                        }
                    }
                }
                if (!foundMenuId) {
                    // Menu not found - user doesn't have access or menu doesn't exist
                    if (mounted) {
                        setMenuScopes([]);
                        setMenuId('');
                        setError(null);
                        setIsLoading(false);
                    }
                    return;
                }
                // Fetch scopes using the menu ID
                const result = await fetchMenuScopesById(foundMenuId);
                if (mounted) {
                    setMenuScopes(result.scopes);
                    setMenuId(foundMenuId);
                    setError(null);
                }
            }
            catch (err) {
                if (mounted) {
                    console.error('Failed to load user scopes:', err);
                    setError(err instanceof Error ? err : new Error('Failed to load scopes'));
                    setMenuScopes([]);
                }
            }
            finally {
                if (mounted) {
                    setIsLoading(false);
                }
            }
        };
        loadScopes();
        return () => {
            mounted = false;
            if (retryTimeout) {
                clearTimeout(retryTimeout);
            }
        };
    }, [menuPathOrId, isMenuId, retryCount]);
    // Check if user has specific scope (by display name or internal name)
    const hasScope = useCallback((scopeNameOrDisplay) => {
        // Super Admin / system role users have all scopes
        if (isAdminUser) {
            return true;
        }
        // Support both formats:
        // 1. Display name: "Create", "Edit", "Delete", "View All"
        // 2. Internal name (legacy): "create", "edit", "delete", "viewAll"
        // Direct match (display name)
        if (menuScopes.includes(scopeNameOrDisplay)) {
            return true;
        }
        // Legacy format mapping: internal name -> display name
        const legacyToDisplayMap = {
            // Basic CRUD operations
            'create': 'Create',
            'add': 'Create',
            'edit': 'Edit',
            'delete': 'Delete',
            'view': 'View',
            'viewAll': 'View All',
            // Export/Print operations
            'print': 'Print',
            'export': 'Export',
            'import': 'Import',
            'download': 'Download',
            'pdf': 'Download PDF',
            'downloadExcel': 'Download Excel',
            'formDownload': 'Form Download',
            // Audit trail
            'logs': 'View Audit Logs',
            // Mapping tree
            'mappingTree': 'Mapping Tree',
            // Report Template Designer — fields are managed separately from models/templates
            'fieldCreate': 'Add Field',
            'fieldEdit': 'Edit Field',
            'fieldDelete': 'Delete Field',
            // Order workflow
            'confirm': 'Confirm',
            'cancel': 'Cancel',
            // Preset management
            'addPreset': 'Add Preset',
            'updatePreset': 'Update Preset',
            'viewPreset': 'View Preset',
            'deletePreset': 'Delete Preset',
            // Approval/Workflow operations
            'approve': 'Approve',
            'forward': 'Forward',
            'approveForward': 'Approve/Forward',
            // Status management
            'toggleStatus': 'Toggle Status',
            'toggleMaintenance': 'Toggle Maintenance',
            'toggleOverride': 'Toggle Override',
            // Block/Unblock operations
            'blockUnblock': 'Block/Unblock',
            // User management actions
            'manageDevices': 'Manage Devices',
            'changePassword': 'Change Password',
            // Vehicle/Customer mapping
            'viewVehicleMapping': 'View Vehicle Mapping',
            'addVehicleMapping': 'Add Vehicle Mapping',
            'viewCustomerMapping': 'View Customer Mapping',
            // Location/Space management
            'tariffSettings': 'Tariff Settings',
            'manageSpaces': 'Manage Spaces',
            'manageLayout': 'Manage Layout',
            'viewLayout': 'View Layout',
            // User management for gates
            'manageUsersView': 'Manage Users View',
            'assignManageUsers': 'Assign Manage Users',
            // Settings tabs
            'common': 'Common',
            'alphabetDigitMaster': 'Alphabet Digit Master',
            'manualEntryCount': 'Manual Entry Count',
            // Check-in/Check-out operations
            'checkout': 'Checkout',
            // Manual entry operations
            'in': 'IN',
            'out': 'OUT',
        };
        const displayName = legacyToDisplayMap[scopeNameOrDisplay];
        if (displayName) {
            return menuScopes.includes(displayName);
        }
        return false;
    }, [isAdminUser, menuScopes]);
    // Create scope object with all permissions
    const scope = useMemo(() => ({
        // Basic CRUD (CommonScopes)
        create: hasScope('Create') || hasScope('Add'),
        update: hasScope('Edit'),
        delete: hasScope('Delete'),
        viewAll: hasScope('View All'),
        view: hasScope('View'),
        // Export/Print (ExportPrintScopes)
        print: hasScope('Print'),
        export: hasScope('Export'),
        pdf: hasScope('Download PDF'),
        downloadExcel: hasScope('Download Excel'),
        formDownload: hasScope('Form Download'),
        // Bulk import (DataTransferScopes)
        import: hasScope('Import'),
        // Audit trail (AuditScopes)
        logs: hasScope('View Audit Logs'),
        // Mapping tree (MappingTreeScopes)
        mappingTree: hasScope('Mapping Tree'),
        // Order workflow (OrderWorkflowScopes)
        confirm: hasScope('Confirm'),
        cancel: hasScope('Cancel'),
        // Approval/Workflow (ApprovalScopes)
        approve: hasScope('Approve'),
        forward: hasScope('Forward'),
        approveForward: hasScope('Approve/Forward'),
        // Status management (StatusScopes)
        toggleStatus: hasScope('Toggle Status'),
        toggleMaintenance: hasScope('Toggle Maintenance'),
        toggleOverride: hasScope('Toggle Override'),
        // Block/Unblock (BlockUnblockScopes)
        blockUnblock: hasScope('Block/Unblock'),
        // Vehicle/Customer mapping (MappingScopes)
        viewVehicleMapping: hasScope('View Vehicle Mapping'),
        addVehicleMapping: hasScope('Add Vehicle Mapping'),
        viewCustomerMapping: hasScope('View Customer Mapping'),
        // Location/Space management (LocationManagementScopes)
        tariffSettings: hasScope('Tariff Settings'),
        manageSpaces: hasScope('Manage Spaces'),
        manageLayout: hasScope('Manage Layout'),
        viewLayout: hasScope('View Layout'),
        // User management for gates (UserManagementScopes)
        manageUsersView: hasScope('Manage Users View'),
        assignManageUsers: hasScope('Assign Manage Users'),
        // Settings tabs (SettingsTabScopes)
        common: hasScope('Common'),
        alphabetDigitMaster: hasScope('Alphabet Digit Master'),
        manualEntryCount: hasScope('Manual Entry Count'),
        // Check-in/Check-out (CheckInOutScopes)
        checkout: hasScope('Checkout'),
        // Manual entry operations (ManualEntryScopes)
        in: hasScope('IN'),
        out: hasScope('OUT'),
        // Download (ExportPrintScopes)
        download: hasScope('Download'),
        // Preset management (PresetManagementScopes)
        addPreset: hasScope('Add Preset'),
        updatePreset: hasScope('Update Preset'),
        viewPreset: hasScope('View Preset'),
        deletePreset: hasScope('Delete Preset'),
    }), [hasScope]);
    // Check if user has any of the specified scopes
    const hasAnyScope = useCallback((...scopeKeys) => {
        return scopeKeys.some(key => scope[key]);
    }, [scope]);
    return {
        scope,
        hasScope,
        hasAnyScope,
        isLoading,
        error,
        menuId,
        menuScopes,
        // Has access if:
        // 1. User is admin (system role - bypass all checks), OR
        // 2. Menu was found (menuId exists), OR
        // 3. Still loading (optimistic access during load)
        hasAnyAccess: isAdminUser || !!menuId || isLoading,
        // Backward compatibility: Expose scope properties at root level (DEPRECATED)
        // These will be removed in a future version - use scope.X instead
        /** @deprecated Use scope.create instead */
        canAdd: scope.create,
        /** @deprecated Use scope.create instead */
        canCreate: scope.create,
        /** @deprecated Use scope.update instead */
        canEdit: scope.update,
        /** @deprecated Use scope.delete instead */
        canDelete: scope.delete,
        /** @deprecated Use scope.view instead */
        canView: scope.view,
        /** @deprecated Use scope.viewAll instead */
        canViewAll: scope.viewAll,
        /** @deprecated Use scope.print instead */
        canPrint: scope.print,
        /** @deprecated Use scope.export instead */
        canExport: scope.export,
        /** @deprecated Use scope.import instead */
        canImport: scope.import,
        /** @deprecated Use scope.logs instead */
        canViewLogs: scope.logs,
        /** @deprecated Use scope.mappingTree instead */
        canMappingTree: scope.mappingTree,
        /** @deprecated Use scope.downloadExcel instead */
        canDownloadExcel: scope.downloadExcel,
        /** @deprecated Use scope.formDownload instead */
        canFormDownload: scope.formDownload,
        /** @deprecated Use scope.approve instead */
        canApprove: scope.approve,
        /** @deprecated Use scope.forward instead */
        canForward: scope.forward,
        /** @deprecated Use scope.approveForward instead */
        canApproveForward: scope.approveForward,
        /** @deprecated Use scope.toggleStatus instead */
        canToggleStatus: scope.toggleStatus,
        /** @deprecated Use scope.toggleMaintenance instead */
        canToggleMaintenance: scope.toggleMaintenance,
        /** @deprecated Use scope.toggleOverride instead */
        canToggleOverride: scope.toggleOverride,
        /** @deprecated Use scope.blockUnblock instead */
        canBlockUnblock: scope.blockUnblock,
        /** @deprecated Use scope.viewVehicleMapping instead */
        canViewVehicleMapping: scope.viewVehicleMapping,
        /** @deprecated Use scope.addVehicleMapping instead */
        canAddVehicleMapping: scope.addVehicleMapping,
        /** @deprecated Use scope.viewCustomerMapping instead */
        canViewCustomerMapping: scope.viewCustomerMapping,
        /** @deprecated Use scope.tariffSettings instead */
        canTariffSettings: scope.tariffSettings,
        /** @deprecated Use scope.manageSpaces instead */
        canManageSpaces: scope.manageSpaces,
        /** @deprecated Use scope.manageLayout instead */
        canManageLayout: scope.manageLayout,
        /** @deprecated Use scope.viewLayout instead */
        canViewLayout: scope.viewLayout,
        /** @deprecated Use scope.manageUsersView instead */
        canManageUsersView: scope.manageUsersView,
        /** @deprecated Use scope.assignManageUsers instead */
        canAssignManageUsers: scope.assignManageUsers,
        /** @deprecated Use scope.common instead */
        canCommon: scope.common,
        /** @deprecated Use scope.alphabetDigitMaster instead */
        canAlphabetDigitMaster: scope.alphabetDigitMaster,
        /** @deprecated Use scope.manualEntryCount instead */
        canManualEntryCount: scope.manualEntryCount,
        /** @deprecated Use scope.checkout instead */
        canCheckout: scope.checkout,
        /** @deprecated Use scope.in instead */
        canIn: scope.in,
        /** @deprecated Use scope.out instead */
        canOut: scope.out,
    };
}
/**
 * Hook to check multiple menu scopes at once
 * Useful for pages that need to check permissions for multiple resources
 *
 * @param menuPaths - Array of menu paths to check (e.g., ['/parking/gates', '/parking/customers'])
 * @returns Object with scopes organized by menu path
 */
export function useMultipleScopes(menuPaths) {
    const [scopesByMenu, setScopesByMenu] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        let mounted = true;
        const loadScopes = async () => {
            try {
                setIsLoading(true);
                // Get menus from cache to find actual IDs
                const menuData = getMenuDataFromCache();
                if (!menuData) {
                    if (mounted) {
                        setScopesByMenu({});
                    }
                    return;
                }
                // Fetch scopes for all menus in parallel
                const results = await Promise.all(menuPaths.map(async (menuPath) => {
                    try {
                        // Try pathToMenuId mapping first
                        let menuId = menuData.pathToMenuId?.[menuPath] || null;
                        // Fallback: Find menu in tree
                        if (!menuId) {
                            const menu = findMenuByPath(menuData.menus, menuPath);
                            menuId = menu?.id || null;
                        }
                        if (!menuId) {
                            console.warn(`[useMultipleScopes] Menu not found for path: ${menuPath}`);
                            return { menuPath, scopes: [] };
                        }
                        const result = await fetchMenuScopesById(menuId);
                        return { menuPath, scopes: result.scopes };
                    }
                    catch (err) {
                        console.warn(`Failed to fetch scopes for menu: ${menuPath}`, err);
                        return { menuPath, scopes: [] };
                    }
                }));
                if (mounted) {
                    const scopesMap = {};
                    results.forEach(({ menuPath, scopes }) => {
                        scopesMap[menuPath] = scopes;
                    });
                    setScopesByMenu(scopesMap);
                }
            }
            catch (err) {
                if (mounted) {
                    console.error('Failed to load user scopes:', err);
                    setScopesByMenu({});
                }
            }
            finally {
                if (mounted) {
                    setIsLoading(false);
                }
            }
        };
        if (menuPaths.length > 0) {
            loadScopes();
        }
        else {
            setIsLoading(false);
        }
        return () => {
            mounted = false;
        };
    }, [menuPaths.join(',')]); // Re-run when menu paths change
    // Generate permissions for each menu
    const permissionsByMenu = useMemo(() => {
        const result = {};
        for (const menuPath of menuPaths) {
            const scopes = scopesByMenu[menuPath] || [];
            result[menuPath] = {
                canViewAll: scopes.includes('View All'),
                canAdd: scopes.includes('Create') || scopes.includes('Add'),
                canEdit: scopes.includes('Edit'),
                canDelete: scopes.includes('Delete'),
                canView: scopes.includes('View'),
                canPrint: scopes.includes('Print'),
                canExport: scopes.includes('Export'),
                canApprove: scopes.includes('Approve'),
                canForward: scopes.includes('Forward'),
                canApproveForward: scopes.includes('Approve/Forward'),
                canDownloadExcel: scopes.includes('Download Excel'),
                canFormDownload: scopes.includes('Form Download'),
                canToggleStatus: scopes.includes('Toggle Status'),
                canBlockUnblock: scopes.includes('Block/Unblock'),
            };
        }
        return result;
    }, [scopesByMenu, menuPaths]);
    return {
        isLoading,
        scopesByMenu,
        permissionsByMenu,
        hasScope: (menuPath, scopeDisplayName) => (scopesByMenu[menuPath] || []).includes(scopeDisplayName),
    };
}
// Legacy exports for backward compatibility
export { useScopes as useScopeCheck };
// Legacy hook that returns hasScope function (for gradual migration)
export function useScopeCheckLegacy(menuName) {
    // This is kept for backward compatibility but should not be used in new code
    console.warn('[useScopeCheckLegacy] This hook is deprecated. Use useScopes instead.');
    const menuScopes = useState([]);
    return {
        hasScope: (scopeName) => menuScopes[0].includes(scopeName),
        hasAnyAccess: true,
    };
}
//# sourceMappingURL=use-scopes.js.map